/* eslint-disable eslint-comments/disable-enable-pair */

/* eslint-disable camelcase */
import { setUser } from '@sentry/browser'
import Cookies from 'js-cookie'
import { get, inRange } from 'lodash'

import Modal from 'antd/es/modal'

import httpClient, {
  CONTACT_ADMINISTRATOR_MESSAGE,
  UNKNOWN_SERVER_ERROR_MESSAGE,
  catchError
} from '@emmacare/common/api/httpClient'
import webSocket from '@emmacare/common/utils/socket'

import {
  isUserAuthorized$,
  setIsManuallyLoggedOut,
  setIsUserAuthorized,
  setUserProfile
} from 'src/store/modules/auth'
import {
  fetchCompanyDiseases,
  fetchCompanyReferences
} from 'src/store/modules/company'

import {
  AUTH_GET_USER_PROFILE,
  AUTH_POST_FORGOT_PASSWORD,
  AUTH_POST_LOGIN,
  AUTH_POST_LOGOUT,
  AUTH_POST_SET_PASSWORD,
  MATCHING_EMAIL_MESSAGE,
  REFRESH_QUERY_PARAM
} from 'src/utils/constants'
import { showInfoModal, showSuccessModal } from 'src/utils/helpers/dialogs'

const setAuthTokens = (access_token = '', refresh_token = '') => {
  Cookies.set('access_token', access_token)
  Cookies.set('refresh_token', refresh_token)
}

const removeAuthTokens = () => {
  Cookies.remove('access_token')
  Cookies.remove('refresh_token')
}

export const fetchUserProfile = () => async (dispatch: any) => {
  try {
    const userProfile = await httpClient.request({
      method: 'get',
      url: {
        pathname: AUTH_GET_USER_PROFILE
      }
    })

    await webSocket.connect(get(userProfile, ['uuid'], null))
    setUser({ email: userProfile?.email })

    dispatch(setUserProfile(userProfile))
  } catch (error: any) {
    catchError(error)
  }
}

export const fetchRequiredData = () => async (dispatch: any) => {
  try {
    // TODO RQ.prefetch
    await Promise.all([
      dispatch(fetchUserProfile()),
      dispatch(
        fetchCompanyDiseases({ query: { sort: 'name:asc', 'per-page': 1000 } })
      ),
      dispatch(
        fetchCompanyReferences({
          query: {
            'filter[enabled]': 'eq:1',
            sort: 'refValue:asc'
          }
        })
      )
      // TODO
    ])
  } catch (error: any) {
    catchError(error)
  }
}

export const loginUser =
  (config: any = {}) =>
  async (dispatch: any) => {
    const { payload = {} } = config

    try {
      const { access_token, refresh_token } = await httpClient.request({
        method: 'post',
        url: {
          pathname: AUTH_POST_LOGIN
        },
        payload
      })

      setAuthTokens(access_token, refresh_token)

      await dispatch(fetchRequiredData())
      dispatch(setIsUserAuthorized(true))
      dispatch(setIsManuallyLoggedOut(false))

      return true
    } catch (error: any) {
      let title = 'Error'

      if (inRange(error?.status, 400, 499)) {
        title = 'Validation Error'
      }

      Modal.error({
        title,
        content: get(error, ['error_description'], UNKNOWN_SERVER_ERROR_MESSAGE)
      })

      return false
    }
  }

export const refreshUser = (refreshToken) => async () => {
  const { access_token, refresh_token } = await httpClient.request({
    method: 'post',
    url: {
      pathname: AUTH_POST_LOGIN
    },
    payload: {
      ...REFRESH_QUERY_PARAM,
      refresh_token: refreshToken
    }
  })

  setAuthTokens(access_token, refresh_token)
}

export const logoutUser = () => async (dispatch: any, getState: any) => {
  const isUserAuthorized = isUserAuthorized$(getState())

  if (!isUserAuthorized) return

  removeAuthTokens()

  await webSocket.disconnect()

  dispatch(setUserProfile({}))
  dispatch(setIsUserAuthorized(false))
  dispatch(setIsManuallyLoggedOut(true))

  setUser(null)

  try {
    await httpClient.request({
      method: 'post',
      url: {
        pathname: AUTH_POST_LOGOUT
      },
      payload: {}
    })
  } catch (_) {}
}

export const forgotPassword =
  (config: any = {}) =>
  async () => {
    const { payload = {} } = config

    try {
      const response = await httpClient.request({
        method: 'post',
        url: {
          pathname: AUTH_POST_FORGOT_PASSWORD
        },
        payload
      })
      const isEmrUser = get(response, ['isEmrUser'], false)

      if (isEmrUser) {
        showInfoModal({
          content: CONTACT_ADMINISTRATOR_MESSAGE
        })
      } else {
        showSuccessModal({
          content: MATCHING_EMAIL_MESSAGE
        })
      }

      return true
    } catch (error: any) {
      catchError(error)

      return false
    }
  }

export const setPassword =
  (config: any = {}) =>
  async () => {
    const { payload = {} } = config

    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: AUTH_POST_SET_PASSWORD
        },
        payload
      })

      return true
    } catch (error: any) {
      catchError(error)

      return false
    }
  }

export const authorizeUser = async (config = {}) => {
  const { params = {}, query = {} } = config

  try {
    return await httpClient.request({
      method: 'get',
      url: {
        pathname: 'auth/authorize',
        params,
        query
      }
    })
  } catch (err) {
    catchError(err)
    return false
  }
}
